const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/metakatReducer.ts';
import Imm from 'immutable';
import { Logger } from '../services/KisLoggerService';
import { decCounter, incCounter } from '../utils/reducerUtils';
import { IActionHandlersMap, IKisApiHandlersMap, makeReducer } from '../defs/actionTypes';
import {
    actionTypes,
    ILoadMainTopicConfigAction,
    ILoadMetaKatBlockAction,
    ILoadMetaKatChildrenAction,
    ILoadMetaKatLookupAction,
    ILoadMetaKatRecordAction,
    ILoadMultiMetakatRecordsAction,
    LoadReaIpsConfigAction,
    LoadStatusTagConfigAction,
    LoadMetaKatRecordByKatFkeyAction,
} from '../actions/metakatActions';
import kisimObjIconReducer from '../components/KisimObjIcon/store/reducer';
import { getColorDef } from '../utils/colorUtils';
import {
    defaultKisApiActionFailure,
    defaultKisApiActionPending,
    defaultKisApiActionSuccess,
    getDataFromPayloadWithListObject,
    RequestStatus,
} from '../utils/redux4Requests';

const Log = Logger('CommonWeb/metakatReducer');

const emptyMap = Imm.Map<string, any>();
const emptyList = Imm.List<any>();

const loadMetaKatBlockHandlers: IKisApiHandlersMap<ILoadMetaKatBlockAction> = {
    pending: (state, action) => {
        state = forgetMetakatBlock(state, action);
        state = defaultKisApiActionPending(state, ['block', action.meta.icode], action);
        return incCounter(state, ['loading', action.meta.icode]);
    },
    success: (state, action) => {
        state = defaultKisApiActionSuccess(state, getDataFromPayloadWithListObject(action), [
            'block',
            action.meta.icode,
        ]);
        state = finalizeLoadMetakatBlock(state, action, false);
        return decCounter(state, ['loading', action.meta.icode]);
    },
    failure: (state, action) => {
        state = defaultKisApiActionFailure(state, ['block', action.meta.icode], action);
        return decCounter(state, ['loading', action.meta.icode]);
    },
};

const loadMetaKatChildrenHandlers: IKisApiHandlersMap<ILoadMetaKatChildrenAction> = {
    pending: (state, action) => {
        state = defaultKisApiActionPending(state, ['children', action.meta.pcode], action);
        return incCounter(state, ['loading', action.meta.pcode]);
    },
    success: (state, action) => {
        state = defaultKisApiActionSuccess(state, getDataFromPayloadWithListObject(action), [
            'children',
            action.meta.pcode,
        ]);
        state = finalizeLoadMetakatBlock(state, action, true);
        return decCounter(state, ['loading', action.meta.pcode]);
    },
    failure: (state, action) => {
        state = defaultKisApiActionFailure(state, ['children', action.meta.pcode], action);
        Log.warn(`Failed to load children of ${action.meta.pcode}`);
        return decCounter(state, ['loading', action.meta.pcode]);
    },
};

const loadMetaKatRecordByKatFkeyHandlers: IKisApiHandlersMap<LoadMetaKatRecordByKatFkeyAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', (action.meta as any).icode ?? (action.meta as any).pcode]);
    },
    success: (state, action) => {
        let rec = Imm.fromJS(action.payload);

        if (action.meta.etag) {
            rec = rec.set('ETAG', action.meta.etag);
        }

        const icode = rec.get('ICODE');
        const pcode = rec.get('PCODE');

        if (!icode || !pcode) {
            Log.warn('Missing mandatory members in payload!');
            return state;
        }

        state = state.setIn(['elements', icode], rec);

        if (!state.hasIn(['structure', pcode])) {
            state = state.setIn(['structure', pcode], emptyList);
        }

        // FIXME this is hard to read, we should use a different function for LoadMetaKatRecordByKatFkeyAction!
        if ((action.meta as any).lookup) {
            // lookup can only be set in meta of LoadMetaKatRecordByKatFkeyAction.
            // That meta has a pcode, not an icode.
            // The record's PCODE is meta.pcode after lookup translation.
            state = state.setIn(['lookups', (action.meta as any).pcode], pcode);
        }

        // FIXME this is not correct! POS is not necessarily unique in a subtree! Moreover, POS may be undefined!
        state = state.setIn(['structure', pcode, parseInt(rec.get('POS'), 10) - 1], icode);
        return decCounter(state, ['loading', (action.meta as any).icode ?? (action.meta as any).pcode]);
    },
    failure: (state, action) => {
        const icode = (action.meta as any).icode ?? (action.meta as any).pcode;
        Log.warn(`Failed to load record: ${icode}`);
        return decCounter(state, ['loading', icode]);
    },
};

const loadMetaKatRecordHandlers: IKisApiHandlersMap<ILoadMetaKatRecordAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', (action.meta as any).icode ?? (action.meta as any).pcode]);
    },
    success: (state, action) => {
        let rec = Imm.fromJS(action.payload);

        if (action.meta.etag) {
            rec = rec.set('ETAG', action.meta.etag);
        }

        const icode = rec.get('ICODE');
        const pcode = rec.get('PCODE');

        if (!icode || !pcode) {
            Log.warn('Missing mandatory members in payload!');
            return state;
        }

        state = state.setIn(['elements', icode], rec);

        if (!state.hasIn(['structure', pcode])) {
            state = state.setIn(['structure', pcode], emptyList);
        }

        // FIXME this is hard to read, we should use a different function for LoadMetaKatRecordByKatFkeyAction!
        if ((action.meta as any).lookup) {
            // lookup can only be set in meta of LoadMetaKatRecordByKatFkeyAction.
            // That meta has a pcode, not an icode.
            // The record's PCODE is meta.pcode after lookup translation.
            state = state.setIn(['lookups', (action.meta as any).pcode], pcode);
        }

        // FIXME this is not correct! POS is not necessarily unique in a subtree! Moreover, POS may be undefined!
        state = state.setIn(['structure', pcode, parseInt(rec.get('POS'), 10) - 1], icode);
        return decCounter(state, ['loading', (action.meta as any).icode ?? (action.meta as any).pcode]);
    },
    failure: (state, action) => {
        const icode = (action.meta as any).icode ?? (action.meta as any).pcode;
        Log.warn(`Failed to load record: ${icode}`);
        return decCounter(state, ['loading', icode]);
    },
};

const loadMetaKatLookupHandlers: IKisApiHandlersMap<ILoadMetaKatLookupAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', action.meta.icode]);
    },
    success: (state, action) => {
        const actualIcode = action.payload && action.payload.ICODE;
        state = state.setIn(['lookups', action.meta.icode], actualIcode);
        return decCounter(state, ['loading', action.meta.icode]);
    },
    failure: (state, action) => {
        Log.warn(`Failed to load lookup of ${action.meta.icode}`);
        return decCounter(state, ['loading', action.meta.icode]);
    },
};

const loadReaIpsConfigHandlers: IKisApiHandlersMap<LoadReaIpsConfigAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', action.meta.icode]);
    },
    success: (state, action) => {
        const payload = Imm.fromJS(action.payload || {});
        const icode = payload?.get('ICODE');

        if (!icode) {
            Log.error('Rea/Ips with missing ICODE!', payload.toJS());
        } else {
            const content = payload.get('CONTENT');
            const reaColors = getColorDef(content, 'ReaColors');
            const ipsColors = getColorDef(content, 'IpsColors');
            const intColors = getColorDef(content, 'IntColors');
            const intAColors = getColorDef(content, 'IntAColors');
            const acpColors = getColorDef(content, 'AcpColors');

            state = state.set('rea', reaColors);
            state = state.set('ips', ipsColors);
            state = state.set('int', intColors);
            state = state.set('intA', intAColors);
            state = state.set('acp', acpColors);
        }
        return decCounter(state, ['loading', action.meta.icode]);
    },
    failure: (state, action) => {
        Log.warn(`Failed to load Rea/Ips configuration`);
        return decCounter(state, ['loading', action.meta.icode]);
    },
};

const loadStatusTagConfigHandlers: IKisApiHandlersMap<LoadStatusTagConfigAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', action.meta.icode]);
    },
    success: (state, action) => {
        const payload = Imm.fromJS(action.payload || {});
        const icode = payload?.get('ICODE');
        if (!icode) {
            Log.error('Statustag with missing ICODE!', payload.toJS());
        } else {
            let statustagConfig = emptyMap;
            (payload.getIn(['CONTENT', 'MultiPar']) || emptyList).forEach((item: Imm.Map<string, any>) => {
                const def = item.get('CONTENT');
                if (def) {
                    const tag = def.get('Tag') || '?';
                    statustagConfig = statustagConfig.set(tag, def.delete('Tag'));
                }
            });
            state = state.set('statustag', statustagConfig);
        }
        return decCounter(state, ['loading', action.meta.icode]);
    },
    failure: (state, action) => {
        Log.warn(`Failed to load Statustag configuration`);
        return decCounter(state, ['loading', action.meta.icode]);
    },
};

const loadMainTopicConfigHandlers: IKisApiHandlersMap<ILoadMainTopicConfigAction> = {
    pending: (state, action) => {
        return incCounter(state, ['loading', action.meta.pcode]);
    },
    success: (state, action) => {
        const list = Imm.fromJS(action.payload?.List || {});
        let mainTopicConfig = emptyMap;

        (list || emptyList)
            .filter((topic: Imm.Map<string, any>) => topic?.get('STATUS') !== 'I')
            .forEach((topic: Imm.Map<string, any>) => {
                const mainTopic = topic?.get('FKEY');

                if (mainTopic) {
                    (topic?.getIn(['CONTENT', 'SubTopics']) || emptyList)
                        .filter((subTopic: Imm.Map<string, any>) => subTopic?.get('IsVisible') === '1')
                        .forEach((subTopic: Imm.Map<string, any>) => {
                            const id = subTopic.get('Id');

                            if (id) {
                                const title = subTopic.get('Title');
                                const accessCode = subTopic.get('AccessCode');

                                mainTopicConfig = mainTopicConfig
                                    .setIn([mainTopic, id, 'title'], title)
                                    .setIn([mainTopic, id, 'accessCode'], accessCode);
                            }
                        });
                }
            });

        state = state.set('mainTopic', mainTopicConfig);
        return decCounter(state, ['loading', action.meta.pcode]);
    },
    failure: (state, action) => {
        Log.warn(`Failed to load MainTopic configurations`);
        return decCounter(state, ['loading', action.meta.pcode]);
    },
};

const loadMultiMetakatRecordsHandlers: IKisApiHandlersMap<ILoadMultiMetakatRecordsAction> = {
    pending: state => {
        state = state.setIn(['multiMetaKat', 'mmkRequestStatus'], RequestStatus.LOADING);
        return incCounter(state, ['loading', 'multiMetaKat']);
    },
    success: (state, action) => {
        const list = Imm.fromJS(action.payload || {});
        let metakatList = emptyMap;
        (list || emptyList).forEach((topic: Imm.Map<string, any>) => {
            const key = topic?.get('Key');
            const subList = topic?.getIn(['List']);
            metakatList = metakatList.setIn([key], subList);
        });
        state = state.set('multiMetaKat', metakatList);
        state = state.setIn(['multiMetaKat', 'mmkRequestStatus'], RequestStatus.SUCCESS);
        return decCounter(state, ['loading', 'multiMetaKat']);
    },
    failure: state => {
        Log.warn(`Failed to load multiMetakat configurations`);
        state = state.setIn(['multiMetaKat', 'mmkRequestStatus'], RequestStatus.FAILED);
        return decCounter(state, ['loading', 'multiMetaKat']);
    },
};

const handlers: IActionHandlersMap = {
    [actionTypes.loadMetaKatBlock.pending]: loadMetaKatBlockHandlers.pending,
    [actionTypes.loadMetaKatBlock.success]: loadMetaKatBlockHandlers.success,
    [actionTypes.loadMetaKatBlock.failure]: loadMetaKatBlockHandlers.failure,
    [actionTypes.loadMetaKatChildren.pending]: loadMetaKatChildrenHandlers.pending,
    [actionTypes.loadMetaKatChildren.success]: loadMetaKatChildrenHandlers.success,
    [actionTypes.loadMetaKatChildren.failure]: loadMetaKatChildrenHandlers.failure,
    [actionTypes.loadMetaKatRecordByKatFkey.pending]: loadMetaKatRecordByKatFkeyHandlers.pending,
    [actionTypes.loadMetaKatRecordByKatFkey.success]: loadMetaKatRecordByKatFkeyHandlers.success,
    [actionTypes.loadMetaKatRecordByKatFkey.failure]: loadMetaKatRecordByKatFkeyHandlers.failure,
    [actionTypes.loadMetaKatRecord.pending]: loadMetaKatRecordHandlers.pending,
    [actionTypes.loadMetaKatRecord.success]: loadMetaKatRecordHandlers.success,
    [actionTypes.loadMetaKatRecord.failure]: loadMetaKatRecordHandlers.failure,
    [actionTypes.loadMetaKatLookup.pending]: loadMetaKatLookupHandlers.pending,
    [actionTypes.loadMetaKatLookup.success]: loadMetaKatLookupHandlers.success,
    [actionTypes.loadMetaKatLookup.failure]: loadMetaKatLookupHandlers.failure,
    [actionTypes.loadReaIpsConfig.pending]: loadReaIpsConfigHandlers.pending,
    [actionTypes.loadReaIpsConfig.success]: loadReaIpsConfigHandlers.success,
    [actionTypes.loadReaIpsConfig.failure]: loadReaIpsConfigHandlers.failure,
    [actionTypes.loadStatusTagConfig.pending]: loadStatusTagConfigHandlers.pending,
    [actionTypes.loadStatusTagConfig.success]: loadStatusTagConfigHandlers.success,
    [actionTypes.loadStatusTagConfig.failure]: loadStatusTagConfigHandlers.failure,
    [actionTypes.loadMainTopicConfig.pending]: loadMainTopicConfigHandlers.pending,
    [actionTypes.loadMainTopicConfig.success]: loadMainTopicConfigHandlers.success,
    [actionTypes.loadMainTopicConfig.failure]: loadMainTopicConfigHandlers.failure,
    [actionTypes.multiloadMetaKatRecords.pending]: loadMultiMetakatRecordsHandlers.pending,
    [actionTypes.multiloadMetaKatRecords.success]: loadMultiMetakatRecordsHandlers.success,
    [actionTypes.multiloadMetaKatRecords.failure]: loadMultiMetakatRecordsHandlers.failure,
    [actionTypes.forgetMetaKatRecord]: forgetMetakatBlock,
};

const metakatInitialState = {
    loading: {}, // Maps pcode -> count
    structure: {},
    elements: {},
    lookups: {}, // Maps virtual icode -> actual icode
};
export const initialState = Imm.fromJS(metakatInitialState);

export const metakatReducer = makeReducer(handlers, initialState, undefined, [kisimObjIconReducer]);

export const metakatReducerMultiMetakat = (multiMKKeysInitialState: Imm.Map<string, string>) =>
    makeReducer(
        handlers,
        Imm.fromJS({
            ...metakatInitialState,
            multiMetaKat: {},
            multiMKKeys: multiMKKeysInitialState,
        }),
        undefined,
        [kisimObjIconReducer]
    );

function finalizeLoadMetakatBlock(
    state: Imm.Map<string, any>,
    action: ILoadMetaKatBlockAction | ILoadMetaKatChildrenAction,
    anyLevel: boolean
) {
    let entries: any = !action.payload ? null : action.payload.List;
    if (!entries || entries.length === 0) {
        return state;
    }
    if (!Array.isArray(entries)) {
        entries = Imm.Map<string, any>(entries)
            .sortBy((_: any, key: string) => Number(key || 0))
            .toList()
            .toArray();
    }
    return state.withMutations((mut: Imm.Map<any, any>) => {
        let baseIcode: string | null = null;
        entries.forEach((entry: any) => {
            mut.setIn(['elements', entry.ICODE], Imm.fromJS(entry));
            if (anyLevel || parseInt(entry.LEVEL, 10) > 1) {
                if (!mut.hasIn(['structure', entry.PCODE])) {
                    mut.setIn(['structure', entry.PCODE], Imm.List());
                }
                mut.setIn(['structure', entry.PCODE, parseInt(entry.POS, 10) - 1], entry.ICODE);
            } else {
                baseIcode = entry.ICODE;
            }
        });

        const metaIcode = (action.meta as any).icode;

        if (action.meta.lookup && baseIcode && metaIcode) {
            if (mut.hasIn(['elements', baseIcode])) {
                mut.setIn(['elements', metaIcode], Imm.fromJS(mut.getIn(['elements', baseIcode]).toJS()));
            }
            if (mut.hasIn(['structure', baseIcode])) {
                mut.setIn(['structure', metaIcode], Imm.fromJS(mut.getIn(['structure', baseIcode]).toJS()));
            }
        }
        if (action.meta.lookup && action.meta.rootRecordExpectedInPayload !== undefined) {
            const virtualIcode = (action.meta as any).pcode ?? (action.meta as any).icode;
            const translatedIcode = action.meta.rootRecordExpectedInPayload ? entries[0]?.ICODE : entries[0]?.PCODE;

            if (virtualIcode && translatedIcode) {
                mut.setIn(['lookups', virtualIcode], translatedIcode);
            }
        }
    });
}

function forgetMetakatBlock(state: Imm.Map<string, any>, action: any = {}) {
    return state.withMutations((mut: Imm.Map<any, any>) => {
        // Delete icode from structure list of block's parent (if applicable)
        if (mut.hasIn(['elements', action.meta.icode])) {
            const rootPcode: string = mut.getIn(['elements', action.meta.icode, 'PCODE']);
            const rootPos: string = mut.getIn(['elements', action.meta.icode, 'POS']);
            if (rootPcode && rootPos && mut.hasIn(['structure', rootPcode])) {
                mut.setIn(['structure', rootPcode, parseInt(rootPos, 10) - 1], null);
            }
        }
        // Recursively walk through existing block structure and delete found elements
        const delElements = [action.meta.icode];
        while (delElements.length > 0) {
            const strIcode = delElements.pop();
            if (mut.hasIn(['structure', strIcode])) {
                mut.getIn(['structure', strIcode]).forEach((subStrIcode: string) => {
                    delElements.push(subStrIcode);
                });
                mut.deleteIn(['structure', strIcode]);
            }
            mut.deleteIn(['elements', strIcode]);
        }
    });
}
